import type { StoreReferralProgram, WalletEntry } from './types';

/** Código de indicação pendente por loja: guardado na entrada pelo link, usado depois do login. */
const KEY = 'pending-store-referral';

type PendingReferral = { slug: string; code: string };

function readAll(): PendingReferral[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(items: PendingReferral[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Sem storage (aba anônima): a indicação só não sobrevive ao login.
  }
}

export function normalizeReferralCode(raw: string): string | null {
  const code = raw.trim().toUpperCase();
  return /^[A-Z0-9]{4,32}$/.test(code) ? code : null;
}

export function savePendingReferral(slug: string, rawCode: string): string | null {
  const code = normalizeReferralCode(rawCode);
  if (!slug || !code) return null;
  writeAll([...readAll().filter(p => p.slug !== slug), { slug, code }]);
  return code;
}

export function getPendingReferral(slug: string): string | null {
  return readAll().find(p => p.slug === slug)?.code ?? null;
}

export function clearPendingReferral(slug: string) {
  writeAll(readAll().filter(p => p.slug !== slug));
}

export function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

/** "5%" ou "R$ 10,00" — o benefício de cada lado, como o cliente lê. */
export function describeReward(
  program: StoreReferralProgram,
  side: 'referrer' | 'referee'
): string {
  if (program.reward_type === 'fixed') {
    return formatBRL(side === 'referrer' ? program.referrer_reward : program.referee_reward);
  }
  const pct = side === 'referrer' ? program.referrer_percent : program.referee_percent;
  return `${pct.toLocaleString('pt-BR')}%`;
}

export function buildShareUrl(sharePath: string, origin = window.location.origin): string {
  return `${origin}${sharePath}`;
}

const ENTRY_TITLE: Record<WalletEntry['origin'], string> = {
  referrer_reward: 'Crédito por indicação',
  referee_reward: 'Bônus de boas-vindas',
  reward_reversal: 'Estorno de crédito',
  order_payment: 'Saldo usado',
  order_refund: 'Saldo devolvido',
};

export function entryTitle(entry: WalletEntry): string {
  if (entry.origin === 'referrer_reward' && entry.related_customer) {
    return `${entry.related_customer} concluiu pedido`;
  }
  return ENTRY_TITLE[entry.origin] ?? 'Movimentação';
}
